import React from 'react';
import { convertDateTime } from 'utils/time';
import TableViewButton from 'components/admin/TableViewButton';
import styles from 'pages/admin/Meets/Meets.module.css';
import { SquarePen } from 'lucide-react';
import { toast } from 'react-toastify';
import { getAgeEvents, getEvents } from '../../api/eventApi';
import { clubFilterOptions } from 'config/admin/filterOptions';
import { inServerEventOrder } from 'utils/eventListOrder';
import { isRelayEvent } from 'utils/relayTeams';
import routes from 'routes';

/**
 * Transforms event data into a format suitable for table display.
 *
 * Processes a list of event objects to add formatted date, time, and a "manage" action
 * link for each event. The action link points to a results aggregation page.
 *
 * @param {Event[]} events - An array of event objects. Each object should include:
 *   - {string} timestamp: The ISO date string of the event (e.g., "2024-12-30T14:30:00").
 *   - {string} eventType: The type of the event (e.g., "100m").
 *   - {string} ageGroup: The age group classification of the event.
 *   - {string} gender: The gender classification of the event.
 *   - {string} roundType: The round type (e.g., "final").
 *   - Any additional properties, which are included in the formatted result as-is.
 * @param {string} clubId - The ID of the club used to construct URLs for managing events.
 * @param {string} meetId - The ID of the result used to construct URLs for managing events.
 *
 * @returns {Array} A list of formatted event objects, each containing:
 *   - {string} date: Formatted date and time of the event (e.g., "2024-12-30 14:30").
 *   - {JSX.Element} manage: A clickable `Link` component directing to the results page.
 *   - Additional properties from the original event object.
 *
 * @example
 * const events = [
 *   {
 *     timestamp: "2024-12-30T14:30:00",
 *     eventType: "100m",
 *     ageGroup: "U18",
 *     gender: "male",
 *     roundType: "final",
 *   },
 * ];
 * const clubId = "123";
 * const meetId = "456";
 * const tableData = eventsToTableData(events, clubId, meetId);
 * // Output:
 * // [
 * //   {
 * //     ...event,
 * //     date: "2024-12-30 14:30",
 * //     manage: <Link to="..." className={styles.viewButton}>Results</Link>,
 * //   },
 * // ]
 *
 * @notes
 * - Uses `convertDateTime` to format the `timestamp` field into date and time components.
 * - Generates URLs using `routes.admin.results.url`.
 * - Assumes `styles.viewButton` is a valid CSS class for the "Results" button.
 */

export let uniqueAgeGroups = [];
export const getUniqueAgeGroups = () => uniqueAgeGroups;

// insures that the age view is correct
export const getUniqueAgeGroupsForFilter = (tableData) => {
  // get the unique ages from the tabledata
  const uniqueAges = [
    ...new Set(
      tableData.flatMap((item) =>
        item.ageGroups.map((ageGroupObj) => ageGroupObj.ageGroup)
      )
    ),
  ];

  const ageOrder = clubFilterOptions.ageGroup.values;

  return uniqueAges.sort((a, b) => ageOrder.indexOf(a) - ageOrder.indexOf(b));
};

// sort the age event page by age
// not gonna do it this way but leave it just in case it can be used later
/*
const sortByAgeGroup = (a, b) => {
  const ageOrder = clubFilterOptions.ageGroup.values;
  if (a.ageGroup !== b.ageGroup) {
    return ageOrder.indexOf(a.ageGroup) - ageOrder.indexOf(b.ageGroup);
  }
  return 0;
};
*/

export const eventsToTableData = (
  events,
  clubId,
  meetId,
  isPending,
  editEventType,
  handleEventView
) => {
  const results = [];
  events.forEach((event) => {
    results.push({
      ...event,
      date: `${convertDateTime(event.timestamp).date} ${convertDateTime(event.timestamp).time}`,
      rounds: getRoundsText(event.rounds),
      eventIDs: event.rounds.filter((r) => r.eventID).map((r) => r.eventID),
      eventType: (
        <div className={styles.eventTypeEditContainer}>
          <div>{event.eventType}</div>
          {isPending && (
            <div
              style={{ height: 16, cursor: 'pointer' }}
              onClick={() => editEventType(event)}
            >
              <SquarePen size={16} color="rgb(207, 44, 71)" />
            </div>
          )}
        </div>
      ),
      eventTypeValue: event.eventType,
      manage: (
        <div className={styles.buttons}>
          <TableViewButton
            handleClick={() => handleEventView(event, event.publishingStatus)}
            style={{ marginRight: 10 }}
          />
        </div>
      ),
    });
  });

  // In the server's event order, which is the club's (utils/eventListOrder).
  return inServerEventOrder(results);
};

export const ageEventsToTableData = (
  events,
  clubId,
  meetId,
  isPending,
  editEventType,
  handleEventView
) => {
  const results = [];
  events.forEach((event) => {
    const eventType = event.eventType;
    event.ageGroups.forEach((ageGroup) => {
      const ageGroupEvent = {
        eventType,
        ageGroup: ageGroup.ageGroup,
        gender: ageGroup.gender,
        rounds: ageGroup.rounds,
        roundType: getAgeRoundsText(ageGroup.rounds),
      };
      results.push({
        ...ageGroup,
        rounds: getAgeRoundsText(ageGroup.rounds),
        eventTypeValue: eventType,
        manage: (
          <div className={styles.buttons}>
            <TableViewButton
              handleClick={() =>
                handleEventView(ageGroupEvent, event.publishingStatus)
              }
              style={{ marginRight: 10 }}
            />
          </div>
        ),
        publishingStatus: event.publishingStatus,
      });
    });
  });
  // In the server's event order, which is the club's (utils/eventListOrder).
  return inServerEventOrder(results);
};

export const eventsToExportTableData = (events) => {
  const results = [];
  let id = 0;
  events.forEach((event) => {
    results.push({
      ...event,
      id,
      isCheck: false,
      status: '',
    });
    id++;
  });
  return results;
};

// Returns the event matching the given URL params (eventType + ageGroup + gender)
// within the specified meet. If currentSelectedEvent already matches, returns it
// without an API call (cache-warm path). Otherwise fetches getEvents(clubId, meetId)
// and finds the match. Returns null if no matching event exists in the meet.
export const resolveEventFromParams = async (
  clubId,
  meetId,
  eventType,
  ageGroup,
  gender,
  currentSelectedEvent
) => {
  // ageGroup can come through as "10" (from dashboard result rows) or "U10"
  // (from event records / Events admin page). Normalize before comparing.
  const normalizeAgeGroup = (value) =>
    String(value ?? '').replace(/^U/i, '');
  const targetAgeGroup = normalizeAgeGroup(ageGroup);

  const matches = (event) =>
    event &&
    event.eventType === eventType &&
    normalizeAgeGroup(event.ageGroup) === targetAgeGroup &&
    event.gender === gender &&
    (event.meetID == null || event.meetID === meetId);

  if (matches(currentSelectedEvent)) {
    return currentSelectedEvent;
  }

  const response = await getEvents(clubId, meetId);
  const events = response?.data || [];
  return events.find(matches) || null;
};

export const getRoundsOptions = (rounds) => {
  return rounds.map((round) => {
    return {
      label: `${round.roundType} ${round.roundNumber}`,
      type: round.roundType,
      value: round.eventID,
    };
  });
};

// Age results aggregate every round of a type into one list, so Heat/Semi
// label as plurals; a meet runs a single final, so Final stays singular.
const AGE_ROUND_LABELS = { Heat: 'Heats', Semi: 'Semis', Final: 'Final' };

export const getAgeRoundLabel = (roundType) =>
  AGE_ROUND_LABELS[roundType] || roundType;

const getRoundsText = (rounds) => {
  let roundsText = [];
  rounds.forEach((item, index) => {
    if (item.eventID && item.eventID !== '') {
      roundsText.push(`${item.roundType}${item.roundNumber}`);
    }
  });
  return roundsText.join(', ');
};

const getAgeRoundsText = (rounds) => {
  return rounds.map((item) => getAgeRoundLabel(item.roundType)).join(', ');
};


export const fetchAllAgeView = async (
  clubId,
  meetId,
  setEvents,
  editEventType,
  handleEventView,
  navigate
) => {
  try {
    const agePublishedEventResponse = await getAgeEvents(clubId, meetId);
    // The age view is individual events only: a relay is read on its Event
    // View's Overall tab (Doug, 2026-10-19). A team's result never reaches this
    // list; a relay whose lanes held athletes (an upload from before the timing
    // app took teams) would, so it is left out here.
    const individualEvents = (agePublishedEventResponse.data || []).filter(
      (event) => !isRelayEvent(event.eventType)
    );
    uniqueAgeGroups = getUniqueAgeGroupsForFilter(individualEvents);
    // console.log('Unique Ages: ' + uniqueAgeGroups);

    const rows = ageEventsToTableData(
      individualEvents,
      clubId,
      meetId,
      true,
      editEventType,
      handleEventView
    );
    setEvents(rows);
    // The age view is built from results, so a meet with no results yet has
    // no rows here even when it has events. The caller uses the count to fall
    // back to the event view. Not returned on an error.
    return rows.length;
  } catch (err) {
    console.log('[fetchAllAgeView] error: ', err);
    if (err.response?.status === 403) {
      // Handle 403 Forbidden - user doesn't have permission
      toast.error(
        err.response?.data?.message ||
          "You don't have permission to view these results"
      );
      navigate(routes.admin.dashboard); // or wherever you want to redirect them
    }
  }
};

export const fetchAllEventView = async (
  clubId,
  meetId,
  setEvents,
  editEventType,
  handleEventView,
  navigate,
  setHasAgeEvents
) => {
  try {
    const publishedEventResponse = await getEvents(clubId, meetId);
    const events = publishedEventResponse.data;
    if (setHasAgeEvents) {
      setHasAgeEvents(events.length > 0 && events[0].hasAgeEvents === true);
    }
    setEvents(
      eventsToTableData(
        events,
        clubId,
        meetId,
        false,
        editEventType,
        handleEventView
      )
    );
  } catch (err) {
    console.log('[fetchAllEventView] error: ', err);
    if (err.response?.status === 403) {
      // Handle 403 Forbidden - user doesn't have permission
      toast.error(
        err.response?.data?.message ||
          "You don't have permission to view these results"
      );
      navigate(routes.admin.dashboard); // or wherever you want to redirect them
    }
  }
};
